import { createFileRoute, Link } from '@tanstack/react-router'
import { css } from '../../../../styled-system/css'

import Reload from '~icons/material-symbols/refresh.svg'
import Search from '~icons/material-symbols/search.svg'
import Close from '~icons/material-symbols/close.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import { button } from '../../../styles/buttonStyles'
import { input, inputButton, inputWithButtonContainer } from '../../../styles/inputStyles'
import { table, tableHead, tableHeadCell, tableRow, tableCell, tableWrapper } from '../../../styles/tableStyles'
import { pageTitle, titleWrapper } from '../../../styles/sectionStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { useGetUser, useGetUsers } from '../../../libs/admin/hooks'
import { formatGender } from '../../../libs/utils/format-gender'
import { formatTimestampJaJst } from '../../../libs/utils/format-timestamp'
import { useRef } from 'react'
import { atom, useAtom } from 'jotai'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { useQueryClient } from '@tanstack/react-query'
import { GetUsersRequest, type UserSensitive } from 'schema/gen/es/chiikipoint/adminconsole/v2/service_pb'

export const Route = createFileRoute('/admin/users/')({
  component: RouteComponent,
})

type SearchCondition = {
  searchBy: 'id' | 'name' | undefined
  value: string | undefined
}

const searchConditionAtom = atom<SearchCondition>({
  searchBy: undefined,
  value: undefined,
})

const idAtom = atom<string>('')
const nameAtom = atom<string>('')

const Toolbar = (props: {
  onReset?: () => void,
  onSubmit?: (data: SearchCondition) => void
}) => {
  // Refs needed to focus the input after reset
  const idInputRef = useRef<HTMLInputElement>(null)
  const userNameInputRef = useRef<HTMLInputElement>(null)

  const [id, setId] = useAtom(idAtom)
  const [name, setName] = useAtom(nameAtom)
  const canSubmit = id !== '' || name !== ''
  const handleSubmit = () => {
    if (props.onSubmit && canSubmit) {
      if (id !== '') {
        props.onSubmit({ searchBy: 'id', value: id })
      } else if (name !== '') {
        props.onSubmit({ searchBy: 'name', value: name })
      }
    }
  }
  const handleReset = () => {
    setId('')
    setName('')
    if (props.onReset) {
      props.onReset() // Reset the search condition in the parent component
    }
  }

  const shouldEnableName = id === ''
  const shouldEnableNameReset = shouldEnableName && name !== ''
  const shouldEnableId = name === ''
  const shouldEnableIdReset = shouldEnableId && id !== ''

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    })}>
      <form
        className={css({
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          alignItems: 'end',
        })}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        onReset={(e) => {
          e.preventDefault()
          handleReset()
        }}
      >
        <div className={css({
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'end',
        })}>
          {/* サブスクリプションID または ユーザーID */}
          <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            width: '400px',
          })}>
            <div className={css({
              fontSize: '12px',
            })}>サブスクリプションID または ユーザーID</div>
            <div className={inputWithButtonContainer()}>
              <input
                type="text"
                placeholder='123456'
                value={id}
                onChange={(e) => setId(e.target.value)}
                className={input()}
                disabled={!shouldEnableId}
                ref={idInputRef}
              />
              <button
                type="button"
                className={inputButton({ semantic: 'danger' })}
                onClick={() => {
                  setId('')
                  if (idInputRef.current) {
                    idInputRef.current.focus()
                  }
                }}
                disabled={!shouldEnableIdReset}
              >
                <Close />
              </button>
            </div>
          </div>

          {/* 名前 */}
          <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            width: '240px',
          })}>
            <div className={css({
              fontSize: '12px',
            })}>利用者氏名</div>
            <div className={inputWithButtonContainer()}>
              <input
                type="text"
                placeholder='春日 太郎'
                className={input()}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!shouldEnableName}
                ref={userNameInputRef}
              />
              <button
                type="button"
                className={inputButton({ semantic: 'danger' })}
                onClick={() => {
                  setName('')
                  if (userNameInputRef.current) {
                    userNameInputRef.current.focus()
                  }
                }}
                disabled={!shouldEnableNameReset}
              >
                <Close />
              </button>
            </div>
          </div>
        </div>
        <button
          type="reset"
          className={button()}
        >
          <Reload />
          リセット
        </button>
        <button
          type="submit"
          className={button({ color: 'primary' })}
          disabled={!canSubmit}
        >
          <Search />
          検索
        </button>
      </form>
      <div className={css({
        fontSize: '14px',
        color: 'text.secondary',
      })}>
        利用者氏名は完全一致検索です。氏名の間には半角スペースが必要です。
      </div>
    </div>
  )
}

const Table = (props: {
  users: UserSensitive[]
  isFetching: boolean
}) => {
  const { users, isFetching } = props;
  return (
    <div className={tableWrapper()}>
      {isFetching && <LoadingOverlay />}
      <table className={table()}>
        <thead className={tableHead({ disabled: users?.length === 0 })}>
          <tr>
            <th className={tableHeadCell()}>サブスクリプションID</th>
            <th className={tableHeadCell()}>氏名</th>
            <th className={tableHeadCell()}>性別</th>
            <th className={tableHeadCell()}>登録住所</th>
            <th className={tableHeadCell()}>生年月日</th>
            <th className={tableHeadCell()} /> {/* Empty cell for the last column */}
          </tr>
        </thead>
        {users.map((user) => {
          return (<tbody key={user.subscriptionId}>
            <tr className={tableRow()}>
              <td className={tableCell()}>{user.subscriptionId}</td>
              <td className={tableCell()}>{user.name}</td>
              <td className={tableCell()}>{formatGender(user.gender)}</td>
              <td className={tableCell()}>{user.cityAddress}</td>
              <td className={tableCell()}>{user.birthday ? formatTimestampJaJst(user.birthday) : ''}</td>
              {/* see detail button */}
              <td className={tableCell()}>
                <div className={css({
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                })}>
                  <Link
                    to={"/admin/users/$subscriptionOrUserId"}
                    params={{ subscriptionOrUserId: user.subscriptionId }}
                  >
                    <button type='button' className={button()}>
                      詳細<ArrowForward />
                    </button>
                  </Link>
                </div>
              </td>
            </tr>
          </tbody>)
        })}
      </table>
    </div>
  )
}

const useUserSearch = (searchCondition: SearchCondition) => {
  const { data: _users, isFetching: isFetchingUsers } = useGetUsers(new GetUsersRequest({
    query: searchCondition.searchBy === 'name' ? { name: searchCondition.value } : undefined,
  }))
  const { data: _user, isFetching: isFetchingUser } = useGetUser({
    subscriptionOrUserId: searchCondition.searchBy === 'id' ? searchCondition.value : undefined
  })
  const isFetching = isFetchingUser || isFetchingUsers
  const nameSearchResult = _users?.users || [];
  const idSearchResult = _user?.user ? [_user.user] : [];
  const result = searchCondition.searchBy === 'name' ? nameSearchResult : idSearchResult;
  return {
    users: result,
    isFetching,
  }
}

function RouteComponent() {
  const queryClient = useQueryClient()
  const [searchCondition, setSearchCondition] = useAtom(searchConditionAtom)
  const { users, isFetching } = useUserSearch(searchCondition);

  const handleReset = () => {
    setSearchCondition({ searchBy: undefined, value: undefined });
  }
  const handleSubmit = (data: SearchCondition) => {
    queryClient.invalidateQueries({ queryKey: ['AdminGetUsers', 'AdminGetUser'] }) // force refetch
    setSearchCondition(data);
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>利用者検索</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <Toolbar
          onReset={handleReset}
          onSubmit={handleSubmit}
        />
        <Table users={users} isFetching={isFetching} />
      </div>
    </div>
  )
}
